// About.jsx
import React from "react";

export default function About() {
  return (
    <div className="bg-gradient-to-b from-gray-50 to-white mx-10">
      <div className="w-full mx-auto">
        {/* Hero / Main Title */}
        <div className="text-center mb-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-2">
            About eBhashaSetu
          </h1>
          <p className="text-xl md:text-2xl font-medium text-blue-700 mb-2">
            Connecting Worlds Through Language
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We help businesses, institutions, and creators break language
            barriers with accurate, culturally relevant, and fast translation &
            localization services — powered by expert linguists and smart
            technology.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-8">
          {[
            {
              title: "Our Mission",
              desc1:
                "To make high-quality multilingual content accessible to everyone — without compromising meaning, tone, technical accuracy, or brand voice.",
              desc2:
                "We believe language should never be a barrier to knowledge, commerce, education, or connection.",
              gradient:
                "from-blue-400 via-indigo-500 via-purple-500 to-pink-400",
            },
            {
              title: "Our Vision",
              desc1:
                "To become the most trusted partner for enterprises, startups, governments, and content creators who need reliable, scalable, and human-centered language solutions across India and beyond.",
              desc2:
                "One document, one website, one campaign — at a time — we help ideas travel further.",
              gradient:
                "from-pink-400 via-purple-500 via-indigo-500 to-blue-400",
            },
          ].map((item, idx) => (
            <div key={idx} className="group relative rounded-2xl overflow-hidden bg-white shadow-lg transition-all duration-500 ease-out hover:shadow-2xl hover:scale-[1.03]"
            >
              {/* Animated gradient border */}
              <div className={`absolute inset-0 rounded-2xl p-[3px] bg-gradient-to-r ${item.gradient} bg-[length:200%_200%] animate-gradient-x transition-opacity duration-500 group-hover:opacity-100`}
              >
                <div className="w-full h-full bg-white rounded-2xl"></div>
              </div>

              {/* Card content */}
              <div
                className="
                  relative z-10 p-8 lg:p-10
                  bg-gradient-to-br from-gray-50/70 to-white
                  transition-all duration-500
                  group-hover:from-gray-100/80 group-hover:to-white
                  flex flex-col
                "
              >
                <h2
                  className="
                    text-2xl font-semibold text-gray-800 mb-5
                    transition-colors duration-500
                    group-hover:text-gray-900
                  "
                >
                  {item.title}
                </h2>
                <p
                  className="
                    text-gray-700 leading-relaxed mb-3
                    transition-colors duration-500
                    group-hover:text-gray-800
                  "
                >
                  {item.desc1}
                </p>
                <p
                  className="
                    text-gray-700 leading-relaxed
                    transition-colors duration-500
                    group-hover:text-gray-800
                  "
                >
                  {item.desc2}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* What We Do – Services Highlights */}
        <div className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            What We Do Best
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Document & Technical Translation",
                desc: "User manuals, APIs, research papers, contracts, medical & legal documents — preserved formatting included.",
                gradient:
                  "from-blue-400 via-indigo-500 via-purple-500 to-pink-400",
                iconColor: "blue",
              },
              {
                title: "Website & App Localization",
                desc: "Full UI/UX localization, SEO-friendly multilingual websites, mobile apps in 20+ Indian & global languages.",
                gradient:
                  "from-indigo-400 via-cyan-400 via-teal-400 to-emerald-400",
                iconColor: "indigo",
              },
              {
                title: "Marketing & Creative Content",
                desc: "Ads, social media, video subtitles, brochures, brand storytelling — culturally adapted, not just translated.",
                gradient:
                  "from-teal-400 via-emerald-400 via-green-400 to-blue-400",
                iconColor: "teal",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="
                  group relative rounded-2xl overflow-hidden
                  bg-white shadow-lg
                  transition-all duration-500 ease-out
                  hover:shadow-2xl hover:scale-[1.03]
                "
              >
                {/* Animated gradient border */}
                <div
                  className={`
                    absolute inset-0 rounded-2xl p-[3px]
                    bg-gradient-to-r ${item.gradient}
                    bg-[length:200%_200%] animate-gradient-x
                    transition-opacity duration-500 group-hover:opacity-100
                  `}
                >
                  <div className="w-full h-full bg-white rounded-2xl"></div>
                </div>

                {/* Card content */}
                <div
                  className="
                    relative z-10
                    bg-gradient-to-br from-gray-50/70 to-white
                    transition-all duration-500
                    group-hover:from-gray-100/80 group-hover:to-white
                    p-8 lg:p-9
                    flex flex-col items-center text-center
                    min-h-[280px]
                  "
                >
                  {/* Icon circle */}
                  <div
                    className={`
                      w-16 h-16 rounded-full
                      bg-${item.iconColor}-100/80
                      flex items-center justify-center
                      mb-6
                      ring-1 ring-${item.iconColor}-200/50
                      transition-all duration-300 group-hover:ring-${item.iconColor}-300/70
                    `}
                  >
                    <svg
                      className={`w-9 h-9 text-${item.iconColor}-600 transition-transform duration-500 group-hover:scale-110`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                      />
                    </svg>
                  </div>

                  <h3
                    className="
                      text-xl lg:text-2xl font-semibold
                      text-gray-800 mb-4
                      transition-colors duration-500
                      group-hover:text-gray-900
                    "
                  >
                    {item.title}
                  </h3>

                  <p
                    className="
                      text-gray-600 leading-relaxed
                      text-base lg:text-[1.05rem]
                      transition-colors duration-500
                      group-hover:text-gray-800
                    "
                  >
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="bg-blue-50 rounded-3xl p-10 md:p-14 mb-20 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-10">
            Why eBhashaSetu?
          </h2>

          <div className="grid md:grid-cols-3 gap-10 max-w-5xl mx-auto">
            {[
              {
                num: "100%",
                text: "Human-first approach — AI only assists, never replaces",
              },
              {
                num: "20+",
                text: "Indian & international languages with native experts",
              },
              {
                num: "24–48h",
                text: "Fast turnaround even for complex projects",
              },
            ].map((item, idx) => (
              <div key={idx} className="space-y-3">
                <div className="text-5xl md:text-6xl font-bold text-blue-600">
                  {item.num}
                </div>
                <p className="text-lg text-gray-700 font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Break Language Barriers?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Whether you need one document translated or a full multilingual
            strategy — we're here to help.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-10 py-5 bg-blue-600 text-white rounded-xl text-lg font-medium hover:bg-blue-700 transition shadow-lg"
          >
            <svg
              className="w-6 h-6 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Contact Us Today
          </a>
        </div>
      </div>
    </div>
  );
}
